import type { Component, ComponentType } from '../types/builder';
import { STATS_BOX_DEFAULT_ICONS } from './statsBoxIcons';

export const createDefaultComponent = (type: ComponentType, position: number): Component => {
  const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  switch (type) {
    case 'picture':
      return {
        id,
        type: 'picture',
        position,
        settings: {
          src: '',
          alt: 'Picture',
          link: '',
          openInNewTab: false,
          borderRadius: 12,
          shadow: 'md',
          alignment: 'center',
          padding: 16,
          animation: 'none',
        },
      };

    case 'pictureSlider':
      return {
        id,
        type: 'pictureSlider',
        position,
        settings: {
          slides: [],
          autoPlay: true,
          autoPlaySpeed: 5000,
          showArrows: true,
          showDots: true,
          openInNewTab: false,
          borderRadius: 12,
          shadow: 'md',
          padding: 16,
        },
      };

    case 'form':
      return {
        id,
        type: 'form',
        position,
        settings: {
          fields: [
            {
              id: 'field-1',
              type: 'text',
              label: 'Name',
              placeholder: 'Enter your name',
              required: true,
            },
            {
              id: 'field-2',
              type: 'email',
              label: 'Email',
              placeholder: 'Enter your email',
              required: true,
            },
          ],
          submitButtonText: 'Submit',
          submitButtonColor: '#3B82F6',
          backgroundColor: 'transparent',
          padding: 24,
        },
      };

    case 'video':
      return {
        id,
        type: 'video',
        position,
        settings: {
          src: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          autoplay: false,
          controls: true,
          loop: false,
          width: '100%',
          height: '400px',
          style: 'default',
        },
      };

    case 'onlineUsers':
      return {
        id,
        type: 'onlineUsers',
        position,
        settings: {
          count: 1247,
          title: 'Players Online',
          backgroundColor: 'transparent',
          textColor: '#ffffff',
          iconColor: '#00ff88',
          showPulse: true,
          animateCount: true,
          countFluctuation: 50,
          alignment: 'center',
          padding: 24,
          style: 'default',
        },
      };

    case 'statsBox':
      return {
        id,
        type: 'statsBox',
        position,
        settings: {
          items: [
            {
              id: `sb-${Date.now()}-1`,
              icon: STATS_BOX_DEFAULT_ICONS.online,
              label: 'ออนไลน์',
              min: 1200000,
              max: 1500000,
              delta: 5000,
              highlight: false,
            },
            {
              id: `sb-${Date.now()}-2`,
              icon: STATS_BOX_DEFAULT_ICONS.winners,
              label: 'ผู้ชนะวันนี้',
              min: 38500,
              max: 68780,
              delta: 1000,
              highlight: false,
            },
            {
              id: `sb-${Date.now()}-3`,
              icon: STATS_BOX_DEFAULT_ICONS.prize,
              label: 'รางวัลวันนี้',
              min: 48589400,
              max: 58954000,
              delta: 100000,
              highlight: true,
            },
          ],
          updateInterval: 1000,
          boxColorTop: '#1FB350',
          boxColorBottom: '#0E6E2C',
          accentColor: '#FFD64A',
          textColor: '#FFFFFF',
          valueFontSize: 28,
          labelFontSize: 14,
          iconSize: 56,
          borderRadius: 14,
          gap: 12,
          padding: 12,
          backgroundColor: 'transparent',
        },
      };

    case 'recentWithdrawals':
      return {
        id,
        type: 'recentWithdrawals',
        position,
        settings: {
          title: 'รายการถอนอัตโนมัติล่าสุด',
          items: [
            {
              id: '1',
              username: '080-XXX-XX78',
              amount: '10,800',
              currency: 'บาท',
              time: '',
              bank: 'ttb',
              status: 'pending',
            },
            {
              id: '2',
              username: '093-XXX-XX50',
              amount: '10,900',
              currency: 'บาท',
              time: '',
              bank: 'kbank',
              status: 'success',
            },
            {
              id: '3',
              username: '083-XXX-XX17',
              amount: '27,400',
              currency: 'บาท',
              time: '',
              bank: 'bay',
              status: 'success',
            },
            {
              id: '4',
              username: '088-XXX-XX93',
              amount: '14,300',
              currency: 'บาท',
              time: '',
              bank: 'ttb',
              status: 'success',
            },
            {
              id: '5',
              username: '089-XXX-XX50',
              amount: '23,600',
              currency: 'บาท',
              time: '',
              bank: 'scb',
              status: 'success',
            },
          ],
          backgroundColor: 'transparent',
          textColor: '#ffffff',
          itemBackgroundColor: 'rgba(20, 80, 50, 0.45)',
          maxItems: 5,
          autoScroll: true,
          scrollSpeed: 3000,
          padding: 16,
          style: 'bank',
          accentColor: '#FFD43B',
          pendingLabel: 'กำลังโอน...',
          successLabel: 'โอนแล้ว',
          amountSuffix: 'บาท',
          autoGenerateDates: true,
        },
      };

    case 'jackpot':
      return {
        id,
        type: 'jackpot',
        position,
        settings: {
          title: 'PRIZE COUNTER',
          amount: 1234567.89,
          currency: 'Points',
          incrementSpeed: 2000,
          incrementAmount: 10,
          backgroundColor: 'transparent',
          textColor: '#ffffff',
          amountColor: '#ffd700',
          showIcon: true,
          alignment: 'center',
          padding: 16,
          style: 'default',
        },
      };

    case 'countdownTimer':
      return {
        id,
        type: 'countdownTimer',
        position,
        settings: {
          title: 'Special Offer Ends In:',
          targetDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          showDays: true,
          showHours: true,
          showMinutes: true,
          showSeconds: true,
          labels: {
            days: 'Days',
            hours: 'Hours',
            minutes: 'Minutes',
            seconds: 'Seconds',
          },
          backgroundColor: 'transparent',
          textColor: '#ffffff',
          timerColor: '#00ff88',
          boxBackgroundColor: '#16213e',
          alignment: 'center',
          padding: 24,
        },
      };

    case 'bannerSlide':
      return {
        id,
        type: 'bannerSlide',
        position,
        settings: {
          slides: [
            {
              id: '1',
              image: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=780&h=560&fit=crop',
              link: '#',
              title: 'Welcome Bonus',
            },
            {
              id: '2',
              image: 'https://images.unsplash.com/photo-1557683311-eac922347aa1?w=780&h=560&fit=crop',
              link: '#',
              title: 'VIP Program',
            },
          ],
          autoPlay: true,
          autoPlaySpeed: 5000,
          showArrows: true,
          showDots: true,
          height: 280,
          padding: 0,
        },
      };

    case 'gameRTP':
      return {
        id,
        type: 'gameRTP',
        position,
        settings: {
          title: 'Progress Tracker',
          games: [
            {
              id: '1',
              name: 'Web Development',
              banner: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=160&h=96&fit=crop',
              rtp: 87.5,
              provider: 'Technology',
            },
            {
              id: '2',
              name: 'Mobile Design',
              banner: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=160&h=96&fit=crop',
              rtp: 92.3,
              provider: 'Design',
            },
            {
              id: '3',
              name: 'Marketing Strategy',
              banner: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=160&h=96&fit=crop',
              rtp: 78.8,
              provider: 'Business',
            },
          ],
          backgroundColor: 'transparent',
          textColor: '#ffffff',
          progressColor: '#00ff88',
          progressBackgroundColor: '#2a2a4e',
          showProvider: true,
          animateRTP: true,
          rtpFluctuation: 0.5,
          padding: 24,
        },
      };

    case 'link':
      return {
        id,
        type: 'link',
        position,
        settings: {
          text: 'FACEBOOK',
          url: 'https://facebook.com',
          openInNewTab: true,
          icon: 'https://cdn-icons-png.flaticon.com/512/124/124010.png',
          backgroundImage: '', // Background image/GIF URL
          backgroundColor: 'transparent',
          textColor: '#000000',
          hoverBackgroundColor: '#e0e0e0',
          iconSize: 64,
          fontSize: 18,
          padding: 8,
          animation: 'none',
        },
      };

    case 'profile':
      return {
        id,
        type: 'profile',
        position,
        settings: {
          avatarUrl: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300&h=300&fit=crop',
          avatarSize: 120,
          backgroundColor: 'transparent',
          alignment: 'center',
          padding: 32,
          style: 'default',
        },
      };

    case 'name':
      return {
        id,
        type: 'name',
        position,
        settings: {
          name: 'Your Name',
          title: 'Professional Title',
          nameSize: 32,
          titleSize: 16,
          nameColor: '#1f2937',
          titleColor: '#6b7280',
          backgroundColor: 'transparent',
          alignment: 'center',
          padding: 24,
          style: 'default',
        },
      };

    case 'bio':
      return {
        id,
        type: 'bio',
        position,
        settings: {
          content: 'This is a short bio or description about yourself. You can write anything you want here to introduce yourself to your visitors.',
          fontSize: 16,
          textColor: '#6b7280',
          backgroundColor: 'transparent',
          alignment: 'center',
          padding: 24,
          style: 'default',
        },
      };

    case 'brandLogo':
      return {
        id,
        type: 'brandLogo',
        position,
        settings: {
          logoUrl: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=400&h=200&fit=crop',
          logoSize: 150,
          link: '',
          openInNewTab: true,
          alignment: 'center',
          padding: 32,
          backgroundColor: 'transparent',
          style: 'default',
        },
      };

    case 'partnerLogos':
      return {
        id,
        type: 'partnerLogos',
        position,
        settings: {
          title: 'Our Partners',
          logos: [
            {
              id: 'logo-1',
              imageUrl: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=200&h=80&fit=crop',
              name: 'Partner 1',
              link: '',
            },
            {
              id: 'logo-2',
              imageUrl: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=200&h=80&fit=crop',
              name: 'Partner 2',
              link: '',
            },
            {
              id: 'logo-3',
              imageUrl: 'https://images.unsplash.com/photo-1572044162444-ad60f128bdea?w=200&h=80&fit=crop',
              name: 'Partner 3',
              link: '',
            },
            {
              id: 'logo-4',
              imageUrl: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?w=200&h=80&fit=crop',
              name: 'Partner 4',
              link: '',
            },
          ],
          logoHeight: 60,
          animationSpeed: 30,
          backgroundColor: 'transparent',
          padding: 40,
          style: 'default',
        },
      };

    case 'leaderboard':
      return {
        id,
        type: 'leaderboard',
        position,
        settings: {
          title: '🏆 Top Winners',
          entries: [
            { id: '1', rank: 1, name: 'Player123', prize: '$10,000', avatarUrl: '' },
            { id: '2', rank: 2, name: 'LuckyPlayer', prize: '$5,000', avatarUrl: '' },
            { id: '3', rank: 3, name: 'BigWinner', prize: '$2,500', avatarUrl: '' },
            { id: '4', rank: 4, name: 'ProGamer', prize: '$1,000', avatarUrl: '' },
            { id: '5', rank: 5, name: 'Champion', prize: '$500', avatarUrl: '' },
          ],
          showAvatar: false,
          backgroundColor: 'transparent',
          textColor: '#1f2937',
          padding: 32,
          style: 'default',
        },
      };

    case 'faq':
      return {
        id,
        type: 'faq',
        position,
        settings: {
          title: 'Frequently Asked Questions',
          items: [
            {
              id: '1',
              question: 'How do I get started?',
              answer: 'Simply sign up for an account and follow the onboarding steps. It only takes a few minutes!',
            },
            {
              id: '2',
              question: 'Is it free to use?',
              answer: 'Yes, we offer a free plan with basic features. Premium plans are available for advanced functionality.',
            },
            {
              id: '3',
              question: 'How can I contact support?',
              answer: 'You can reach our support team via email at support@example.com or through our live chat feature.',
            },
          ],
          backgroundColor: 'transparent',
          textColor: '#1f2937',
          padding: 32,
          style: 'default',
        },
      };

    case 'socialIcons':
      return {
        id,
        type: 'socialIcons',
        position,
        settings: {
          icons: [
            { id: '1', platform: 'instagram', url: 'https://instagram.com/yourusername' },
            { id: '2', platform: 'twitter', url: 'https://twitter.com/yourusername' },
            { id: '3', platform: 'facebook', url: 'https://facebook.com/yourusername' },
            { id: '4', platform: 'tiktok', url: 'https://tiktok.com/@yourusername' },
          ],
          iconSize: 40,
          iconColor: '#1f2937',
          spacing: 16,
          backgroundColor: 'transparent',
          alignment: 'center',
          padding: 16,
        },
      };

    case 'testimonial':
      return {
        id,
        type: 'testimonial',
        position,
        settings: {
          avatarUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
          name: 'Sarah Johnson',
          role: 'CEO, Tech Company',
          rating: 5,
          review: 'This is an amazing product! It has completely transformed the way we do business. Highly recommended to anyone looking for a quality solution.',
          backgroundColor: 'transparent',
          textColor: '#1f2937',
          starColor: '#fbbf24',
          avatarSize: 80,
          padding: 24,
        },
      };

    case 'googleReviews':
      return {
        id,
        type: 'googleReviews',
        position,
        settings: {
          title: 'What Our Guests Say',
          reviews: [
            {
              id: 'review-1',
              authorName: 'สมศักดิ์ มานะกิจ',
              authorImage: '',
              rating: 5,
              reviewText: 'งานดีมาก บรรยากาศสวยงาม อาหารอร่อย แนะนำเลยครับ!',
              date: 'มกราคม 2025',
            },
            {
              id: 'review-2',
              authorName: 'วิไลวรรณ ศรีสุข',
              authorImage: '',
              rating: 5,
              reviewText: 'ประทับใจมากค่ะ การจัดงานเป็นระเบียบ ทีมงานน่ารักมากๆ',
              date: 'ธันวาคม 2024',
            },
            {
              id: 'review-3',
              authorName: 'ประเสริฐ วงษ์ประเสริฐ',
              authorImage: '',
              rating: 5,
              reviewText: 'สถานที่สวย บริการดีเยี่ยม คุ้มค่ามากครับ',
              date: 'พฤศจิกายน 2024',
            },
          ],
          autoPlay: true,
          autoPlaySpeed: 5000,
          backgroundColor: 'transparent',
          textColor: '#1f2937',
          starColor: '#fbbf24',
          cardBackgroundColor: '#ffffff',
          padding: 32,
        },
      };

    case 'survey':
      return {
        id,
        type: 'survey',
        position,
        settings: {
          title: 'Customer Satisfaction Survey',
          description: 'We value your feedback! Please take a moment to answer these questions.',
          // Legacy single-page format (default)
          questions: [
            {
              id: crypto.randomUUID(),
              type: 'rating',
              question: 'How satisfied are you with our service?',
              description: 'Rate from 1 to 5 stars',
              required: true,
              maxRating: 5,
            },
            {
              id: crypto.randomUUID(),
              type: 'dropdown',
              question: 'How did you hear about us?',
              description: 'Select one option',
              required: true,
              options: ['Google Search', 'Social Media', 'Friend Referral', 'Advertisement', 'Other'],
            },
            {
              id: crypto.randomUUID(),
              type: 'text',
              question: 'What is your email address?',
              description: 'We will use this to send you updates',
              required: true,
              validation: {
                type: 'email',
                errorMessage: 'Please enter a valid email address',
              },
            },
            {
              id: crypto.randomUUID(),
              type: 'date',
              question: 'When would you like us to contact you?',
              description: 'Select a preferred date',
              required: false,
            },
            {
              id: crypto.randomUUID(),
              type: 'textarea',
              question: 'Any additional comments or suggestions?',
              description: 'Feel free to share your thoughts',
              required: false,
            },
          ],
          // Multi-page sections (disabled by default for backward compatibility)
          enableSections: false,
          showProgressBar: true,
          submitButtonText: 'Submit Survey',
          submitButtonColor: '#3b82f6',
          backgroundColor: 'transparent',
          textColor: '#1f2937',
          padding: 32,
          showResults: true,
        },
      };

    // 🎵 Music Player Component
    case 'musicPlayer':
      return {
        id,
        type: 'musicPlayer',
        position,
        settings: {
          title: '🎵 Our Song',
          audioUrl: '',
          songName: 'Perfect',
          artistName: 'Ed Sheeran',
          coverImage: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400',
          autoPlay: false,
          loop: true,
          style: 'default',
          backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          textColor: '#ffffff',
          accentColor: '#ec4899',
          padding: 32,
        },
      };

    case 'spinWheel':
      return {
        id,
        type: 'spinWheel',
        position,
        settings: {
          title: '🎰 Spin to Win!',
          subtitle: 'Try your luck and win amazing prizes!',
          mode: 'freeSpin',
          prizes: [
            { id: '1', label: '10% Discount', color: '#FFD700', probability: 30, code: 'DISCOUNT10' },
            { id: '2', label: 'Free Shipping', color: '#FF6B6B', probability: 25, code: 'FREESHIP' },
            { id: '3', label: '20% Discount', color: '#4ECDC4', probability: 20, code: 'DISCOUNT20' },
            { id: '4', label: 'Free Gift', color: '#95E1D3', probability: 15, code: 'FREEGIFT' },
            { id: '5', label: 'Try Again', color: '#CCCCCC', probability: 10, code: '' },
          ],
          buttonText: '🎯 SPIN NOW',
          winMessage: '🎉 Congratulations! You won {prize}! Your code: {code}',
          
          // Contact Form
          requireContact: true,
          requirePhone: true,
          requireEmail: false,
          requireName: true,
          
          // Restrictions
          maxSpinsPerIP: 1,
          maxSpinsPerPhone: 1,
          
          // Code to Spin
          codePrefix: 'SPIN2024-',
          
          // Design
          wheelSize: 300,
          spinDuration: 4000,
          backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          textColor: '#ffffff',
          accentColor: '#FFD700',
          padding: 32,
          style: 'default',
        },
      };

    default:
      throw new Error(`Unknown component type: ${type}`);
  }
};

